import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import axios from "axios";
import ProjectCard from "../components/ProjectCard";
import { PageSEO, FAQSchema } from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const faqs = [
  {
    question: "What type of projects does Mist Media produce?",
    answer: "Mist Media produces content across film, television, and digital platforms, spanning genres including drama, horror, thriller, comedy, and more. Our projects include feature films, series, and digital-first content."
  },
  {
    question: "How can I collaborate with Mist Media?",
    answer: "We are always open to collaborations with talented writers, directors, producers, and actors. Visit our Contact page to get in touch with our team about potential projects and partnerships."
  },
  {
    question: "Where is Mist Media based?",
    answer: "Mist Media is based in Los Angeles, California, with projects spanning locations worldwide."
  }
];

export default function HomePage() {
  const [projects, setProjects] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed`).catch(() => {});
        const [projRes, settingsRes] = await Promise.all([
          axios.get(`${API}/projects`),
          axios.get(`${API}/settings`),
        ]);
        setProjects(projRes.data);
        setSettings(settingsRes.data);
      } catch (e) {
        console.error("Failed to fetch data", e);
      } finally {
        setLoaded(true);
      }
    };
    fetchData();
  }, []);

  const featured = projects.filter((p) => p.featured);
  const scrollToProjects = () => {
    document.getElementById("featured-projects")?.scrollIntoView({ behavior: "smooth" });
  };

  const heroVideo = settings?.hero_video_url || "";
  const heroImage = settings?.hero_image_url || "/api/uploads/hero-fallback.jpg";

  return (
    <div data-testid="home-page">
      <PageSEO
        path="/"
        description="Mist Media is a future-forward production company creating bold stories across film, television, and digital. Explore our projects including The Flock, Hackathon, Dark Circles, and more."
        schema={FAQSchema(faqs)}
      />

      {/* ═══ HERO ═══ */}
      <section ref={heroRef} className="h-screen w-full relative overflow-hidden flex items-center justify-center" aria-label="Hero section">
        <motion.div className="absolute inset-0" style={{ y: heroY }}>
          {heroVideo ? (
            <video
              data-testid="hero-video"
              autoPlay
              muted
              loop
              playsInline
              poster={heroImage}
              className="absolute inset-0 w-full h-full object-cover"
              aria-hidden="true"
            >
              <source src={heroVideo} type="video/mp4" />
            </video>
          ) : (
            <img src={heroImage} alt="Mist Media - Film production background" className="absolute inset-0 w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-[#050505]/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/50 via-transparent to-[#050505]/50" />
        </motion.div>

        <motion.div className="relative z-10 text-center px-6" style={{ opacity: heroOpacity }}>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-[10px] sm:text-xs uppercase tracking-[0.4em] text-[#71717A] font-bold mb-8"
          >
            Film &middot; Television &middot; Digital
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="font-['Syne'] text-[4rem] sm:text-[6rem] md:text-[8rem] lg:text-[10rem] font-extrabold tracking-[-0.06em] leading-[0.85] text-white"
          >
            MIST
          </motion.h1>
          <motion.span
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.65 }}
            className="block font-['Syne'] text-[4rem] sm:text-[6rem] md:text-[8rem] lg:text-[10rem] font-extrabold tracking-[-0.06em] leading-[0.85] text-transparent"
            style={{ WebkitTextStroke: "1.5px rgba(255,255,255,0.35)" }}
            aria-label="Media"
          >
            MEDIA
          </motion.span>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="mt-10 text-sm sm:text-base text-[#A1A1AA] font-light max-w-lg mx-auto leading-relaxed"
          >
            {settings?.hero_subtitle || "We tell stories that move people. A future-forward production company crafting bold narratives across every screen."}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-12 flex items-center justify-center gap-4"
          >
            <Link
              to="/projects"
              data-testid="hero-explore-btn"
              className="btn-press group inline-flex items-center gap-3 px-8 py-3.5 bg-white text-[#050505] text-xs font-bold tracking-[0.2em] uppercase rounded-none hover:bg-[#38BDF8] hover:text-[#050505] transition-all duration-300"
            >
              Explore Projects
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/about"
              className="btn-press inline-flex items-center gap-3 px-8 py-3.5 border border-white/20 text-white text-xs font-bold tracking-[0.2em] uppercase rounded-none hover:border-white/50 transition-all duration-300"
            >
              Our Story
            </Link>
          </motion.div>
        </motion.div>

        <motion.button
          onClick={scrollToProjects}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
          data-testid="scroll-indicator"
          aria-label="Scroll to featured projects"
        >
          <span className="text-[9px] uppercase tracking-[0.3em] text-[#52525B] font-bold">Scroll</span>
          <div className="w-px h-8 bg-gradient-to-b from-[#52525B] to-transparent" />
        </motion.button>

        <div className="absolute bottom-8 right-8 hidden lg:block" aria-hidden="true">
          <p className="text-[9px] uppercase tracking-[0.3em] text-[#3f3f46] font-bold writing-vertical">
            Est. 2022
          </p>
        </div>
      </section>

      {/* ═══ FEATURED PROJECTS ═══ */}
      <section id="featured-projects" className="py-28 md:py-40 px-6 md:px-12 lg:px-24" data-testid="featured-projects-section" aria-label="Featured projects">
        <div className="max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex items-end justify-between mb-16"
          >
            <div>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-8 h-px bg-[#38BDF8]" aria-hidden="true" />
                <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#38BDF8]">Featured Work</p>
              </div>
              <h2 className="font-['Syne'] text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-white">
                Our Projects
              </h2>
            </div>
            <Link
              to="/projects"
              data-testid="view-all-projects-link"
              className="hidden md:inline-flex items-center gap-2 text-xs text-[#A1A1AA] hover:text-white transition-colors tracking-[0.2em] uppercase font-bold group"
            >
              View All
              <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>

          {loaded && featured.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-3">
              {featured.map((project, i) => (
                <div
                  key={project.id}
                  className={
                    i === 0 ? "lg:col-span-7" :
                    i === 1 ? "lg:col-span-5" :
                    i === 2 ? "lg:col-span-4" :
                    "lg:col-span-4"
                  }
                >
                  <ProjectCard project={project} index={i} large={i === 0} />
                </div>
              ))}
            </div>
          )}

          <div className="md:hidden mt-10 text-center">
            <Link to="/projects" className="inline-flex items-center gap-2 text-xs text-[#A1A1AA] hover:text-white transition-colors tracking-[0.2em] uppercase font-bold">
              View All Projects <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ MARQUEE ═══ */}
      <section className="border-y border-[#1A1A1A] overflow-hidden py-6" aria-hidden="true">
        <div className="marquee-track flex gap-12 animate-marquee whitespace-nowrap">
          {["DRAMA", "HORROR", "THRILLER", "COMEDY", "SCI-FI", "MYSTERY", "DARK COMEDY", "FILM", "TELEVISION", "DIGITAL", "DRAMA", "HORROR", "THRILLER", "COMEDY", "SCI-FI", "MYSTERY"].map((tag, i) => (
            <span key={i} className="text-xs uppercase tracking-[0.3em] font-bold text-[#27272A]">{tag}</span>
          ))}
        </div>
      </section>

      {/* ═══ STATEMENT ═══ */}
      <section className="py-28 md:py-40 px-6 md:px-12 lg:px-24" aria-label="About Mist Media">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-7"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-8 h-px bg-[#38BDF8]" aria-hidden="true" />
                <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#38BDF8]">Who We Are</p>
              </div>
              <h2 className="font-['Syne'] text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white leading-[1.1]">
                We create stories that challenge, inspire, and push the boundaries of what is possible on screen.
              </h2>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:col-span-5 flex flex-col justify-end"
            >
              <p className="text-sm text-[#A1A1AA] leading-relaxed mb-8">
                From psychological thrillers to dark comedies, our portfolio spans the full spectrum of bold storytelling. We believe the best narratives live at the intersection of art and entertainment.
              </p>
              <Link
                to="/about"
                data-testid="learn-more-link"
                className="btn-press group inline-flex items-center gap-3 text-xs text-white tracking-[0.2em] uppercase font-bold border-b border-[#27272A] hover:border-[#38BDF8] pb-3 transition-all duration-300 self-start"
              >
                Learn More
                <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ FAQ Section (SEO content) ═══ */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-24 border-t border-[#1A1A1A]" aria-label="Frequently asked questions">
        <div className="max-w-[900px] mx-auto">
          <div className="flex items-center gap-4 mb-10">
            <div className="w-8 h-px bg-[#38BDF8]" aria-hidden="true" />
            <h2 className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#38BDF8]">FAQ</h2>
          </div>
          <div className="space-y-8">
            {faqs.map((faq, i) => (
              <article key={i} className="pb-8 border-b border-[#141414] last:border-0">
                <h3 className="font-['Syne'] text-lg md:text-xl font-bold text-white mb-3">{faq.question}</h3>
                <p className="text-sm text-[#A1A1AA] leading-relaxed">{faq.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CTA BANNER ═══ */}
      <section className="py-28 md:py-32 px-6 md:px-12 lg:px-24 border-t border-[#1A1A1A] relative overflow-hidden" aria-label="Call to action">
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#0A0A0A] to-[#050505]" />
        <div className="max-w-[1400px] mx-auto relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#38BDF8] mb-6">Ready to collaborate?</p>
            <h2 className="font-['Syne'] text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white mb-8 max-w-2xl mx-auto">
              Let's create something extraordinary together.
            </h2>
            <Link
              to="/contact"
              data-testid="cta-contact-link"
              className="btn-press group inline-flex items-center gap-3 px-10 py-4 bg-white text-[#050505] text-xs font-bold tracking-[0.2em] uppercase rounded-none hover:bg-[#38BDF8] transition-all duration-300"
            >
              Get in Touch
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
